'use client';

import type { PlateEditor } from 'platejs/react';

import type { NodeEntry, Path, TElement } from 'platejs';
import { KEYS, PathApi } from 'platejs';

import { clearFieldMarksAtBlock } from '@/components/editor/clear-field-marks';

const setList = (
  editor: PlateEditor,
  type: string,
  entry: NodeEntry<TElement>
) => {
  editor.tf.setNodes(
    editor.api.create.block({
      indent: 1,
      listStyleType: type,
    }),
    {
      at: entry[1],
    }
  );
};

const setBlockMap: Record<
  string,
  (editor: PlateEditor, type: string, entry: NodeEntry<TElement>) => void
> = {
  [KEYS.ol]: setList,
  [KEYS.ul]: setList,
};

const createBlockquote = (editor: PlateEditor) => ({
  children: [editor.api.create.block({ type: KEYS.p })],
  type: KEYS.blockquote,
});

const selectBlockquoteStart = (editor: PlateEditor, path: Path) => {
  const start = editor.api.start(path.concat([0]));

  if (start) {
    editor.tf.select(start);
  }
};

const insertListBlock = (editor: PlateEditor, type: string) => {
  editor.tf.insertNodes(
    editor.api.create.block({
      indent: 1,
      listStyleType: type,
    }),
    { select: true },
  );
};

type InsertBlockOptions = {
  upsert?: boolean;
};

/** Insert or transform a block from the slash menu (field-tier types only). */
export const insertBlock = (
  editor: PlateEditor,
  type: string,
  options: InsertBlockOptions = {},
) => {
  const { upsert = false } = options;

  editor.tf.withoutNormalizing(() => {
    const block = editor.api.block();

    if (!block) return;

    const [currentNode, path] = block;
    const isCurrentBlockEmpty = editor.api.isEmpty(currentNode);
    const currentBlockType = getBlockType(currentNode);
    const isSameBlockType = type === currentBlockType;

    if (upsert && isCurrentBlockEmpty && isSameBlockType) {
      return;
    }

    if (type === KEYS.blockquote) {
      if (upsert && isCurrentBlockEmpty && currentBlockType === KEYS.p) {
        editor.tf.setNodes(createBlockquote(editor), { at: path });
        selectBlockquoteStart(editor, path);
        return;
      }

      const insertPath = PathApi.next(path);

      editor.tf.insertNodes(createBlockquote(editor), { at: insertPath });

      if (!isSameBlockType && isCurrentBlockEmpty) {
        editor.tf.removeNodes({ at: path });
      }

      selectBlockquoteStart(
        editor,
        isCurrentBlockEmpty && !isSameBlockType ? path : insertPath,
      );

      return;
    }

    if (type === KEYS.hr) {
      editor.tf.insertNodes(editor.api.create.block({ type: KEYS.hr }), {
        at: PathApi.next(path),
      });

      if (isCurrentBlockEmpty) {
        editor.tf.removeNodes({ at: path });
      }

      return;
    }

    if (type === KEYS.ul || type === KEYS.ol) {
      if (upsert && isCurrentBlockEmpty) {
        setBlockType(editor, type);
        return;
      }

      insertListBlock(editor, type);

      if (!isSameBlockType && isCurrentBlockEmpty) {
        editor.tf.removeNodes({ previousEmptyBlock: true });
      }

      return;
    }

    if (upsert && isCurrentBlockEmpty) {
      setBlockType(editor, type);
      return;
    }

    editor.tf.insertNodes(editor.api.create.block({ type }), {
      at: PathApi.next(path),
      select: true,
    });

    if (!isSameBlockType && isCurrentBlockEmpty) {
      editor.tf.removeNodes({ at: path });
    }
  });
};

/** Set block type for field-tier editors (paragraph, headings, lists). */
export const setBlockType = (
  editor: PlateEditor,
  type: string,
  { at }: { at?: Path } = {}
) => {
  editor.tf.withoutNormalizing(() => {
    const setEntry = (entry: NodeEntry<TElement>) => {
      const [node, path] = entry;

      if (node[KEYS.listType]) {
        editor.tf.unsetNodes([KEYS.listType, 'indent'], { at: path });
      }
      if (type in setBlockMap) {
        setBlockMap[type](editor, type, entry);
      } else if (node.type !== type) {
        editor.tf.setNodes({ type }, { at: path });
      }

      clearFieldMarksAtBlock(editor, path);
    };

    if (at) {
      const entry = editor.api.node<TElement>(at);
      if (entry) {
        setEntry(entry);
        return;
      }
    }

    editor.api.blocks({ mode: 'lowest' }).forEach((entry) => {
      setEntry(entry);
    });
  });
};

export const getBlockType = (block: TElement) => {
  if (block[KEYS.listType]) {
    if (block[KEYS.listType] === KEYS.ol) {
      return KEYS.ol;
    }
    return KEYS.ul;
  }

  return block.type;
};
